
import { supabase } from './supabase';

export interface Policy {
    id: string;
    user_id: string;
    name: string;
    content: string;
    created_at: string;
}

export const policyService = {
    // 获取用户所有政策
    async getAllPolicies(): Promise<Policy[]> {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return [];

        const { data, error } = await supabase
            .from('policies')
            .select('*')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false });

        if (error) {
            console.error('Error fetching policies:', error);
            return [];
        }

        return data || [];
    },

    // 获取用户的默认政策 (获取最新的一个)
    async getUserPolicy(): Promise<Policy | null> {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return null;

        const { data, error } = await supabase
            .from('policies')
            .select('*')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false })
            .limit(1)
            .single();

        if (error) {
            if (error.code === 'PGRST116') return null; // No rows found
            console.error('Error fetching policy:', error);
            return null;
        }

        return data;
    },

    // 创建新政策
    async createPolicy(name: string, content: string): Promise<Policy | null> {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not logged in');

        const { data, error } = await supabase
            .from('policies')
            .insert({
                user_id: user.id,
                name,
                content
            })
            .select()
            .single();

        if (error) throw error;
        return data;
    },

    // 更新政策
    async updatePolicy(id: string, name: string, content: string): Promise<Policy | null> {
        const { data, error } = await supabase
            .from('policies')
            .update({ name, content })
            .eq('id', id)
            .select()
            .single();

        if (error) throw error;
        return data;
    },

    // 删除单个政策
    async deletePolicy(id: string): Promise<void> {
        const { error } = await supabase
            .from('policies')
            .delete()
            .eq('id', id);

        if (error) throw error;
    },

    // 保存或更新政策 (兼容旧逻辑)
    async savePolicy(content: string, name?: string): Promise<Policy | null> {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not logged in');

        const existing = await this.getUserPolicy();

        if (existing) {
            return this.updatePolicy(existing.id, name || existing.name, content);
        } else {
            return this.createPolicy(name || 'Default Policy', content);
        }
    },

    // 删除所有政策 (兼容旧逻辑)
    async clearPolicy(): Promise<void> {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { error } = await supabase
            .from('policies')
            .delete()
            .eq('user_id', user.id);

        if (error) throw error;
    }
};
